/**
 * Login Page Component
 *
 * File-level:
 * - Handles user authentication with email/mobile and password using API integration
 *
 * Notes:
 * - Modified to open a ForgotPasswordModal when the "Forgot Password?" link is clicked.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { API_CONFIG } from '../config/api.config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Mail, Phone, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useApi';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { toast } from 'sonner';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState<'email' | 'mobile'>('email');
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    password: ''
  });
  const navigate = useNavigate();

  const { login, loading, error, clearError } = useAuth();

  // Forgot password modal state
  const [forgotOpen, setForgotOpen] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Validate input type
    let credentials;
    if (loginType === 'email') {
      credentials = { email: formData.email, password: formData.password };
    } else {
      // For mobile login, validate if it's actually a mobile number
      const mobileValue = formData.mobile.trim();
      // Check if it looks like an email (contains @)
      if (mobileValue.includes('@')) {
        // User entered email in mobile field, use email instead
        credentials = { email: mobileValue, password: formData.password };
      } else {
        // It's a mobile number
        credentials = { mobile: mobileValue, password: formData.password };
      }
    }

    console.log('🔄 Starting login process...');
    const result = await login(credentials);

    if (result && result.success) {
      console.log('✅ Login successful, storing tokens...');
      console.log('📦 Login result:', result);

      // Clear any existing admin token to prevent conflicts
      localStorage.removeItem('adminToken');

      // Store authentication token and user data
      if (result.token) {
        localStorage.setItem('authToken', result.token);
      } else {
        localStorage.setItem('authToken', 'mock-jwt-token-' + Date.now());
      }

      if (result.user) {
        localStorage.setItem('userData', JSON.stringify(result.user));
      }

      console.log('✅ Tokens stored, redirecting to dashboard...');
      console.log('📍 Current path:', window.location.pathname);

      // Force redirect to dashboard
      setTimeout(() => {
        window.location.href = '/#/dashboard';
      }, 100);

    } else {
      console.log('❌ Login failed, showing error...');
      // Don't show alert - the error is already displayed in the form
    }
  };

  /**
   * handleForgotSave
   *
   * @description Handler passed to ForgotPasswordModal.
   * Calls the backend API to save the forgot password request.
   *
   * @param payload any
   */
  async function handleForgotSave(payload: any) {
    try {
      // Call the backend API to save the forgot password request
      const response = await fetch(`${API_CONFIG.getBackendOrigin()}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit request');
      }

      const result = await response.json();
      console.log('Forgot password request saved:', result);
      
      // Success toast is shown by the ForgotPasswordModal component
    } catch (error: any) {
      console.error('Failed to save forgot password request:', error);
      toast.error(error.message || 'Failed to submit request. Please try again.');
      throw error; // Re-throw so the modal can handle it
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87] py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex justify-center">
          <div className="flex items-center justify-center w-full">
            <Card className="w-full max-w-md border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#7c3aed] to-[#ec4899] text-white rounded-t-lg">
                <CardTitle className="text-2xl text-center">Login to Partner Nest</CardTitle>
                <CardDescription className="text-white/90 text-center">Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="flex gap-2 mb-4">
                    <Button type="button" variant={loginType === 'email' ? 'default' : 'outline'} onClick={() => setLoginType('email')} className="flex-1 bg-transparent">
                      <Mail className="w-4 h-4 mr-2" /> Email
                    </Button>
                    <Button type="button" variant={loginType === 'mobile' ? 'default' : 'outline'} onClick={() => setLoginType('mobile')} className="flex-1 bg-transparent">
                      <Phone className="w-4 h-4 mr-2" /> Mobile
                    </Button>
                  </div>

                  {loginType === 'email' ? (
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium">Email Address</label>
                      <Input id="email" type="email" placeholder="Enter your email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={loading} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label htmlFor="mobile" className="text-sm font-medium">Mobile Number</label>
                      <Input id="mobile" type="tel" placeholder="Enter your mobile number" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} required disabled={loading} />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium">Password</label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required disabled={loading} />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)} disabled={loading}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="text-right">
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-sm text-white/90 hover:text-white">Forgot Password?</button>
                  </div>

                  <Button type="submit" className="w-full bg-[#7c3aed] hover:bg-[#6d25d0]" disabled={loading}>
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Logging in...</>
                    ) : (
                      'Login'
                    )}
                  </Button>

                  <div className="text-center text-sm">
                    Don't have an account?{' '}
                    <Link to="/register" className="text-white/90 hover:text-white font-medium">Register here</Link>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} onSave={handleForgotSave} />
    </div>
  );
}
